import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';
import { PLANTING_PDF_ENAMEL as T } from './plantingPdfEnamel';

const fe = StyleSheet.create({
  page: {
    padding: 0,
    paddingTop: 36,
    paddingBottom: 56,
    fontSize: 10,
    fontFamily: T.body,
    color: T.ink,
    backgroundColor: T.paper,
  },
  masthead: {
    marginTop: -36,
  },
  banner: {
    backgroundColor: T.lodenDark,
    paddingTop: 22,
    paddingBottom: 20,
    paddingHorizontal: 36,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  bannerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    paddingRight: 16,
  },
  logo: {
    width: 42,
    height: 42,
  },
  kicker: {
    fontFamily: T.mono,
    fontSize: 8,
    letterSpacing: 1.4,
    color: T.sage,
    marginBottom: 4,
  },
  bannerTitle: {
    fontFamily: T.display,
    fontSize: 22,
    color: T.cream,
  },
  metaBlock: {
    alignItems: 'flex-end',
  },
  metaLabel: {
    fontFamily: T.mono,
    fontSize: 7,
    letterSpacing: 1.2,
    color: T.sage,
    textTransform: 'uppercase',
  },
  metaValue: {
    fontFamily: T.bodyBold,
    fontSize: 10,
    color: T.cream,
    marginTop: 2,
  },
  rustRule: {
    height: 4,
    backgroundColor: T.rust,
  },
  body: {
    paddingHorizontal: 36,
    paddingTop: 22,
    paddingBottom: 12,
  },
  planName: {
    fontFamily: T.display,
    fontSize: 24,
    color: T.ink,
    marginBottom: 6,
  },
  leadLine: {
    fontSize: 10,
    color: T.inkSoft,
    marginBottom: 10,
  },
  mixLine: {
    fontFamily: T.bodyBold,
    fontSize: 11,
    color: T.loden,
    marginBottom: 4,
  },
  placeLine: {
    fontSize: 10,
    color: T.inkSoft,
    marginBottom: 16,
  },
  tiles: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 22,
  },
  tile: {
    flex: 1,
    borderWidth: 2,
    borderColor: T.ink,
    backgroundColor: T.paper2,
    paddingVertical: 8,
    paddingHorizontal: 8,
  },
  tileLabel: {
    fontFamily: T.mono,
    fontSize: 7,
    letterSpacing: 1.1,
    color: T.mute,
    marginBottom: 4,
  },
  tileValue: {
    fontFamily: T.display,
    fontSize: 12,
    color: T.ink,
  },
  sectionKicker: {
    fontFamily: T.mono,
    fontSize: 8,
    letterSpacing: 1.4,
    color: T.mute,
    marginBottom: 8,
  },
  cart: {
    borderWidth: 2,
    borderColor: T.ink,
    marginBottom: 22,
  },
  cartHead: {
    flexDirection: 'row',
    backgroundColor: T.loden,
    paddingVertical: 7,
    paddingHorizontal: 10,
  },
  cartHeadCell: {
    fontFamily: T.mono,
    fontSize: 7,
    letterSpacing: 1.1,
    color: T.cream,
  },
  cartRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 10,
    backgroundColor: T.paper2,
    borderTopWidth: 1,
    borderTopColor: T.ink,
  },
  cartRowAlt: {
    backgroundColor: T.khaki,
  },
  cartName: {
    fontFamily: T.bodyBold,
    fontSize: 10,
    color: T.ink,
  },
  cartRate: {
    fontFamily: T.mono,
    fontSize: 7,
    letterSpacing: 0.8,
    color: T.mute,
    marginTop: 3,
  },
  cartQty: {
    fontFamily: T.display,
    fontSize: 14,
    color: T.ink,
    textAlign: 'right',
  },
  cartBags: {
    fontFamily: T.mono,
    fontSize: 7,
    letterSpacing: 0.8,
    color: T.mute,
    textAlign: 'right',
    marginTop: 3,
  },
  colItem: { flex: 1.6 },
  colQty: { width: 78 },
  colBags: { flex: 1.1 },
  totalBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: T.khaki,
    borderWidth: 2,
    borderColor: T.ink,
    borderTopWidth: 0,
    paddingVertical: 10,
    paddingHorizontal: 10,
    marginBottom: 22,
  },
  totalLabel: {
    fontFamily: T.mono,
    fontSize: 8,
    letterSpacing: 1.2,
    color: T.mute,
  },
  totalValue: {
    fontFamily: T.display,
    fontSize: 18,
    color: T.ink,
  },
  siteGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  siteTile: {
    width: '48%',
    borderWidth: 2,
    borderColor: T.ink,
    backgroundColor: T.paper2,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginBottom: 8,
  },
  plotTable: {
    borderWidth: 2,
    borderColor: T.ink,
    marginBottom: 18,
  },
  plotHead: {
    flexDirection: 'row',
    backgroundColor: T.khaki,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderBottomWidth: 2,
    borderBottomColor: T.ink,
  },
  plotRow: {
    flexDirection: 'row',
    paddingVertical: 7,
    paddingHorizontal: 10,
    borderTopWidth: 1,
    borderTopColor: T.ink,
  },
  colPlot: { flex: 2 },
  colAcres: { width: 56, textAlign: 'right' },
  colLoc: { flex: 2 },
  cell: {
    fontSize: 9,
    color: T.ink,
  },
  note: {
    fontSize: 9,
    color: T.inkSoft,
    marginBottom: 12,
  },
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 36,
    backgroundColor: T.lodenDark,
    paddingHorizontal: 36,
    justifyContent: 'center',
  },
  footerText: {
    fontFamily: T.mono,
    fontSize: 7,
    letterSpacing: 0.8,
    color: T.sage,
  },
});

function MetaTile({ label, value }) {
  if (!value) return null;
  return (
    <View style={fe.tile}>
      <Text style={fe.tileLabel}>{label}</Text>
      <Text style={fe.tileValue}>{value}</Text>
    </View>
  );
}

function SiteTile({ label, value }) {
  if (!value) return null;
  return (
    <View style={fe.siteTile} wrap={false}>
      <Text style={fe.tileLabel}>{label}</Text>
      <Text style={fe.cell}>{value}</Text>
    </View>
  );
}

export function PlantingFieldEditionPdfDocument({ data, logoUrl }) {
  const {
    generatedAtLabel,
    userDisplayName,
    groupName,
    groupNote,
    placeLabel,
    clubName,
    isClubGroup,
    zoneLabel,
    soilTypeLabel,
    soilDrainageLabel,
    soilPh,
    soilNotes,
    plots = [],
    totalAcres,
    mixName,
    season,
    windowLabel,
    frostLabel,
    materials = [],
    totalMaterialLabel,
  } = data;
  const acresLine = plots.length > 0
    ? `${plots.length} plot${plots.length === 1 ? '' : 's'} · ${totalAcres} acres`
    : `${totalAcres} acres`;
  const seasonLabel = season ? String(season).toUpperCase() : '';
  const hasSite = Boolean(
    placeLabel
    || zoneLabel
    || frostLabel
    || soilTypeLabel
    || soilDrainageLabel
    || soilPh
    || soilNotes
    || (isClubGroup && clubName)
  );

  return (
    <Document title={`Planting plan — ${groupName}`} author="Yona">
      <Page size="LETTER" style={fe.page}>
        <View style={fe.masthead}>
          <View style={fe.banner}>
            <View style={fe.bannerLeft}>
              {logoUrl ? <Image style={fe.logo} src={logoUrl} /> : null}
              <View>
                <Text style={fe.kicker}>YONA PLANTING ASSISTANT · CO-OP LIST</Text>
                <Text style={fe.bannerTitle}>Planting plan</Text>
              </View>
            </View>
            <View style={fe.metaBlock}>
              {userDisplayName ? (
                <>
                  <Text style={fe.metaLabel}>Prepared for</Text>
                  <Text style={fe.metaValue}>{userDisplayName}</Text>
                </>
              ) : null}
              <Text style={[fe.metaLabel, { marginTop: userDisplayName ? 8 : 0 }]}>Date</Text>
              <Text style={fe.metaValue}>{generatedAtLabel}</Text>
            </View>
          </View>
          <View style={fe.rustRule} />
        </View>

        <View style={fe.body}>
          <Text style={fe.planName}>{groupName || 'Untitled plan'}</Text>
          <Text style={fe.leadLine}>
            Take this list to any seed counter. Stores are omitted so you can shop where you like.
          </Text>
          {mixName ? <Text style={fe.mixLine}>{mixName}</Text> : null}
          <Text style={fe.placeLine}>
            {[acresLine, placeLabel, isClubGroup ? clubName : ''].filter(Boolean).join('  ·  ')}
          </Text>
          {groupNote ? <Text style={fe.note}>{groupNote}</Text> : null}

          <View style={fe.tiles}>
            <MetaTile label="ACRES" value={totalAcres && totalAcres !== '—' ? String(totalAcres) : ''} />
            <MetaTile label="ZONE" value={zoneLabel} />
            <MetaTile label="WINDOW" value={windowLabel} />
            <MetaTile label="SEASON" value={seasonLabel} />
          </View>

          <Text style={fe.sectionKicker}>TAKE TO THE COUNTER</Text>
          <View style={[fe.cart, totalMaterialLabel ? { marginBottom: 0 } : null]}>
            <View style={fe.cartHead}>
              <Text style={[fe.colItem, fe.cartHeadCell]}>ITEM</Text>
              <Text style={[fe.colQty, fe.cartHeadCell, { textAlign: 'right' }]}>QTY</Text>
              <Text style={[fe.colBags, fe.cartHeadCell, { textAlign: 'right' }]}>BAGS</Text>
            </View>
            {materials.map((item, index) => (
              <View
                key={`${item.name}-${index}`}
                style={index % 2 === 1 ? [fe.cartRow, fe.cartRowAlt] : fe.cartRow}
                wrap={false}
              >
                <View style={fe.colItem}>
                  <Text style={fe.cartName}>{item.name}</Text>
                  {item.rate ? <Text style={fe.cartRate}>{item.rate}</Text> : null}
                </View>
                <View style={fe.colQty}>
                  <Text style={fe.cartQty}>{item.qty}</Text>
                </View>
                <View style={fe.colBags}>
                  <Text style={fe.cartBags}>{item.bags}</Text>
                </View>
              </View>
            ))}
          </View>
          {totalMaterialLabel ? (
            <View style={fe.totalBar}>
              <Text style={fe.totalLabel}>SEED + FERTILIZER</Text>
              <Text style={fe.totalValue}>{totalMaterialLabel}</Text>
            </View>
          ) : null}

          {hasSite ? (
            <>
              <Text style={fe.sectionKicker}>SITE</Text>
              <View style={fe.siteGrid}>
                <SiteTile label="PLACE" value={placeLabel} />
                <SiteTile label="USDA ZONE" value={zoneLabel} />
                <SiteTile label="FROST" value={frostLabel} />
                <SiteTile label="SOIL" value={soilTypeLabel} />
                <SiteTile label="DRAINAGE" value={soilDrainageLabel} />
                <SiteTile label="pH" value={soilPh} />
                <SiteTile label="NOTES" value={soilNotes} />
                <SiteTile label="CLUB" value={isClubGroup ? clubName : ''} />
              </View>
            </>
          ) : null}

          {plots.length > 0 ? (
            <>
              <Text style={fe.sectionKicker}>PLOTS</Text>
              <View style={fe.plotTable}>
                <View style={fe.plotHead}>
                  <Text style={[fe.colPlot, fe.cartHeadCell, { color: T.ink }]}>PLOT</Text>
                  <Text style={[fe.colAcres, fe.cartHeadCell, { color: T.ink }]}>ACRES</Text>
                  <Text style={[fe.colLoc, fe.cartHeadCell, { color: T.ink }]}>LOCATION</Text>
                </View>
                {plots.map((plot, index) => (
                  <View key={`${plot.name}-${index}`} style={fe.plotRow} wrap={false}>
                    <Text style={[fe.colPlot, fe.cell]}>{plot.name}</Text>
                    <Text style={[fe.colAcres, fe.cell]}>{plot.acres}</Text>
                    <Text style={[fe.colLoc, fe.cell]}>{plot.location || '—'}</Text>
                  </View>
                ))}
              </View>
            </>
          ) : null}
        </View>

        <View style={fe.footer} fixed>
          <Text style={fe.footerText}>GENERATED BY YONA PLANTING ASSISTANT · YONA.APP</Text>
        </View>
      </Page>
    </Document>
  );
}
